// Iter 233 — Schedule sub-views with hit/miss tracking + filters.
//
// Two curated client-side derivations of the existing /schedule/day payload:
//
//   • <Top20PicksList> — Round-robin one-pick-per-game algorithm:
//     1. For every fixture, pick its single highest-confidence market.
//     2. Sort fixtures by that market's confidence (DESC).
//     3. Apply "easy to predict" bias: Low-risk + ≥80% picks float to
//        the very top.
//     4. Take 20 unique-fixture picks.
//     5. If fewer than 20 unique fixtures exist, fill remaining slots
//        from the 2nd-best market on the highest-confidence fixtures
//        (round 2), then 3rd-best (round 3), etc.
//     Filters: market types, leagues, min confidence, easy-only.
//
//   • <X5OddsBuilder> — Highest-confidence Double-Chance + Halftime
//     accumulator stacked until implied odds ≥ 5.0x.  Up to 25 legs.
//
// Both views auto-snapshot to /api/picks/snapshot on mount so the new
// /api/picks/performance endpoint can compute rolling hit-rate history.

import { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Calculator, Sparkles, TrendingUp, ExternalLink, Filter, RotateCcw, Trophy } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import { LivePickTracker } from "./LivePickTracker";
import { weekStartIso } from "../lib/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = {
  Low:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  Medium: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  High:   "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

const fmtKickoff = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const impliedOdds = (confidencePct) => {
  const c = Math.max(2, Math.min(99, Number(confidencePct) || 0));
  return Math.max(1.05, Math.min(50, 100 / c));
};

// Flatten with full context attached.
const flattenMarkets = (data) => {
  if (!data?.leagues) return [];
  const out = [];
  for (const lg of data.leagues) {
    for (const f of lg.fixtures || []) {
      if (!f.has_prediction) continue;
      for (const m of f.markets || []) {
        out.push({
          match_id: f.match_id,
          fixture_id: f.fixture_id,
          league_id: lg.league_id,
          league_name: lg.league_name,
          league_country: lg.league_country,
          // Iter 275 — flows through so <X5OddsBuilder> can enforce
          // the Betway-only league gate before ranking picks.  Absent
          // on payloads from older backend builds → defaults to true
          // so behaviour on stale servers is unchanged.
          betway_eligible: lg.betway_eligible !== false,
          home: f.home_team?.name,
          away: f.away_team?.name,
          kickoff_iso: f.kickoff_iso,
          market_type: m.market_type,
          prediction: m.prediction,
          confidence: m.confidence,
          risk_level: m.risk_level,
          outcome: m.outcome,
          rationale: m.rationale,
        });
      }
    }
  }
  return out;
};

// "Easy to predict" score: pure confidence, with Low-risk getting a
// +5 bias and High-risk a -5 penalty.  This pushes safer picks to
// the very top while keeping the ordering broadly confidence-driven.
const easyScore = (m) => {
  let s = Number(m.confidence) || 0;
  if (m.risk_level === "Low") s += 5;
  else if (m.risk_level === "High") s -= 5;
  return s;
};

// =============================================================================
// Round-robin one-pick-per-game algorithm
// =============================================================================
//
// 1. Group markets by match_id.
// 2. Inside each group, sort markets by easyScore DESC → a *ranked
//    list of markets per match*.
// 3. Round 1: collect each match's #1 market, sort across matches by
//    easyScore DESC.
// 4. If fewer than 20, Round 2: each match's #2 market, sorted same way.
// 5. Repeat for round 3, 4, … until we have 20 (or run out).
//
// Filters are applied to the FLAT pool first so they affect both
// per-match ranking and round selection.

const buildTop20 = (allMarkets, filters) => {
  // 1. Apply filters
  const pool = allMarkets.filter((m) => {
    // Iter 275c — early-stage Betway league gate (same rule as x5):
    // Top 20 Picks are marketed as "the day's best BETS", so every
    // fixture must be placeable on Betway.
    if (m.betway_eligible === false) return false;
    if (filters.minConfidence != null && m.confidence < filters.minConfidence) return false;
    if (filters.easyOnly && !(m.risk_level === "Low" && m.confidence >= 80)) return false;
    if (filters.markets?.size && !filters.markets.has(m.market_type)) return false;
    if (filters.leagues?.size && !filters.leagues.has(String(m.league_id))) return false;
    return true;
  });

  // 2. Group by match_id, sort each group's markets by easyScore DESC.
  const byMatch = new Map();
  for (const m of pool) {
    const arr = byMatch.get(m.match_id) || [];
    arr.push(m);
    byMatch.set(m.match_id, arr);
  }
  for (const arr of byMatch.values()) {
    arr.sort((a, b) => easyScore(b) - easyScore(a));
  }

  // 3. Round-robin: take markets[round] from every match, sort, take
  //    until we hit 20.
  const picked = [];
  let round = 0;
  while (picked.length < 20) {
    const slice = [];
    for (const [, arr] of byMatch) {
      if (arr[round]) slice.push({ ...arr[round], _round: round });
    }
    if (!slice.length) break;
    slice.sort((a, b) => easyScore(b) - easyScore(a));
    for (const m of slice) {
      picked.push(m);
      if (picked.length >= 20) break;
    }
    round += 1;
    if (round > 12) break; // safety — almost no fixture has 12 markets
  }
  return picked;
};

// =============================================================================
// Hit / Miss summary tile (live, computed from outcome field)
// =============================================================================

const HitMissBar = ({ picks, testid }) => {
  const hits = picks.filter((p) => p.outcome === true).length;
  const misses = picks.filter((p) => p.outcome === false).length;
  const pending = picks.length - hits - misses;
  const settled = hits + misses;
  const rate = settled ? Math.round((hits / settled) * 100) : null;

  return (
    <div className="flex items-center gap-3 text-xs font-tabular" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Trophy className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-muted-foreground">Hit rate</span>
        <span className="font-bold text-foreground" data-testid={`${testid}-rate`}>
          {rate != null ? `${rate}%` : "—"}
        </span>
      </div>
      <span className="text-emerald-300" data-testid={`${testid}-hits`}>{hits} hit</span>
      <span className="text-rose-300" data-testid={`${testid}-misses`}>{misses} miss</span>
      <span className="text-muted-foreground" data-testid={`${testid}-pending`}>{pending} pending</span>
    </div>
  );
};

// Rolling N-day performance (server-side snapshots).
// Iter 275f — Default lookback bumped from 7 → 30 days.  Users
// complained they couldn't see any "previous results" — the old
// 7-day window hid every archived hit older than a week (the vast
// majority of the archive on rolling weeks after a pause in
// compilation).  30 days keeps the panel visually compact but
// surfaces the historical hit-rate that was already sitting in
// `db.daily_picks_snapshots`.
const PerformancePanel = ({ type, days = 30 }) => {
  const [perf, setPerf] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/picks/performance?type=${type}&days=${days}`)
      .then((r) => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then((d) => { if (!cancelled) setPerf(d); })
      .catch((e) => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, [type, days]);

  if (err) return null; // silent — not critical
  if (!perf || !perf.rows?.length) {
    return (
      <div
        className="text-[11px] text-muted-foreground italic mb-3 px-1"
        data-testid={`perf-${type}-empty`}
      >
        Hit/miss history will appear here after the first snapshot is settled.
      </div>
    );
  }
  const agg = perf.aggregate || {};
  return (
    <Card
      className="border-white/10 bg-white/[0.02] mb-3"
      data-testid={`perf-${type}-panel`}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">
            Last {perf.days}d performance (archived)
          </div>
          <div className="text-xs font-tabular flex items-center gap-3">
            <span>
              <span className="text-emerald-300 font-bold" data-testid={`perf-${type}-agg-rate`}>
                {agg.hit_rate_pct != null ? `${agg.hit_rate_pct}%` : "—"}
              </span>
              <span className="text-muted-foreground ml-1">hit rate</span>
            </span>
            <span className="text-muted-foreground">
              {agg.hits || 0}W · {agg.misses || 0}L · {agg.pending || 0} pending
            </span>
          </div>
        </div>
        {/* Iter 275f — Show up to 14 recent daily cells (grid auto-flows on
            narrower viewports).  Previously slice(0,7) which hid all
            archived days older than a week. */}
        <div className="grid grid-cols-7 gap-1.5">
          {perf.rows.slice(0, 14).map((row) => {
            const tone =
              row.hit_rate_pct == null
                ? "bg-white/[0.04]"
                : row.hit_rate_pct >= 70
                ? "bg-emerald-500/15 border-emerald-500/30"
                : row.hit_rate_pct >= 50
                ? "bg-amber-500/15 border-amber-500/30"
                : "bg-rose-500/15 border-rose-500/30";
            return (
              <div
                key={row.date}
                className={`rounded px-2 py-1.5 text-center border border-white/5 ${tone}`}
                title={`${row.date}: ${row.hits}W ${row.misses}L ${row.pending}P`}
              >
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">
                  {row.date.slice(5).replace("-", "/")}
                </div>
                <div className="text-sm font-bold font-tabular">
                  {row.hit_rate_pct != null ? `${row.hit_rate_pct}%` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Idempotent snapshot poster — safe to call on every mount.
const snapshotPicks = (date, type, picks) => {
  if (!date || !picks?.length) return;
  fetch(`${API_URL}/api/picks/snapshot`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      date,
      type,
      list_key: "default",
      picks: picks.map((p) => ({
        match_id: String(p.match_id || ""),
        fixture_id: p.fixture_id != null ? Number(p.fixture_id) : null,
        league_id: p.league_id,
        league_name: p.league_name || "",
        league_country: p.league_country || "",
        home: p.home || "",
        away: p.away || "",
        kickoff_iso: p.kickoff_iso,
        market_type: p.market_type || "",
        prediction: p.prediction || "",
        confidence: p.confidence || 0,
        risk_level: p.risk_level || null,
        outcome: p.outcome != null ? Boolean(p.outcome) : null,
      })),
    }),
  }).catch(() => { /* non-fatal */ });
};

// =============================================================================
// Filter chips ================================================================
// =============================================================================

const FilterBar = ({
  allMarketTypes, allLeagues,
  filters, onChange, onReset,
}) => {
  const [open, setOpen] = useState(false);
  const activeCount =
    (filters.markets?.size || 0) +
    (filters.leagues?.size || 0) +
    (filters.minConfidence > 0 ? 1 : 0) +
    (filters.easyOnly ? 1 : 0);

  const toggle = (setName, value) => {
    const next = new Set(filters[setName]);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange({ ...filters, [setName]: next });
  };

  return (
    <Card className="border-white/10 mb-3" data-testid="top20-filters">
      <CardContent className="p-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-sm"
          data-testid="top20-filters-toggle"
        >
          <span className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-cyan-300" />
            <span className="font-semibold">Filters</span>
            {activeCount > 0 && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-cyan-500/30 text-cyan-300 bg-cyan-500/5">
                {activeCount} active
              </Badge>
            )}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {open ? "Hide" : "Show"}
          </span>
        </button>

        {open && (
          <div className="mt-3 space-y-3 border-t border-white/5 pt-3">
            {/* Min confidence */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">
                  Min confidence
                </div>
                <div className="text-xs font-tabular font-bold" data-testid="top20-filters-min-confidence-value">
                  {filters.minConfidence}%
                </div>
              </div>
              <input
                type="range"
                min={0} max={99} step={1}
                value={filters.minConfidence}
                onChange={(e) => onChange({ ...filters, minConfidence: Number(e.target.value) })}
                className="w-full accent-emerald-500"
                data-testid="top20-filters-min-confidence"
              />
            </div>

            {/* Easy only */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.easyOnly}
                onChange={(e) => onChange({ ...filters, easyOnly: e.target.checked })}
                className="accent-emerald-500"
                data-testid="top20-filters-easy-only"
              />
              <span className="text-sm">Easy to predict only <span className="text-muted-foreground text-xs">(Low risk + ≥80% confidence)</span></span>
            </label>

            {/* Market types */}
            {allMarketTypes.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-1.5">
                  Markets ({filters.markets.size || allMarketTypes.length}/{allMarketTypes.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {allMarketTypes.map((mt) => {
                    const active = filters.markets.has(mt);
                    return (
                      <button
                        type="button"
                        key={mt}
                        onClick={() => toggle("markets", mt)}
                        data-testid={`top20-filters-market-${mt.replace(/\s+/g, "-").toLowerCase()}`}
                        className={[
                          "text-[11px] px-2 py-1 rounded-full border transition",
                          active
                            ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200 font-semibold"
                            : "bg-white/[0.02] border-white/10 text-muted-foreground hover:bg-white/[0.05]",
                        ].join(" ")}
                      >
                        {mt}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Leagues (capped at top 20 by fixture count to keep UI sane) */}
            {allLeagues.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-1.5">
                  Leagues ({filters.leagues.size || allLeagues.length}/{allLeagues.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {allLeagues.map((lg) => {
                    const active = filters.leagues.has(String(lg.id));
                    const flag = countryToFlag(lg.country) || "🌍";
                    return (
                      <button
                        type="button"
                        key={lg.id}
                        onClick={() => toggle("leagues", String(lg.id))}
                        data-testid={`top20-filters-league-${lg.id}`}
                        className={[
                          "text-[11px] px-2 py-1 rounded-full border transition flex items-center gap-1",
                          active
                            ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200 font-semibold"
                            : "bg-white/[0.02] border-white/10 text-muted-foreground hover:bg-white/[0.05]",
                        ].join(" ")}
                      >
                        <span>{flag}</span>
                        <span className="truncate max-w-[140px]">{lg.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {activeCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="text-xs h-7"
                data-testid="top20-filters-reset"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset all filters
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Top-20 Picks  ===============================================================
// =============================================================================

const DEFAULT_FILTERS = {
  minConfidence: 0,
  easyOnly: false,
  markets: new Set(),
  leagues: new Set(),
};

// Iter 236 — Same locked-snapshot pattern as x5 (Iter 235): once the
// unfiltered Top 20 is produced for a day, it must NOT reshuffle when
// live data drifts.  Filtered views (markets / leagues / minConfidence
// / easyOnly toggled) remain exploratory and re-derive client-side —
// they are NOT persisted because each user can pick a different filter
// combo at any time.

const filtersAreDefault = (f) =>
  f.minConfidence === 0 &&
  f.easyOnly === false &&
  (!f.markets || f.markets.size === 0) &&
  (!f.leagues || f.leagues.size === 0);

export const Top20PicksList = ({ data, date, onPickFixture }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const all = useMemo(() => flattenMarkets(data), [data]);

  const allMarketTypes = useMemo(() => {
    const s = new Set();
    all.forEach((m) => s.add(m.market_type));
    return Array.from(s).sort();
  }, [all]);

  // Top 30 leagues by fixture count.
  const allLeagues = useMemo(() => {
    const m = new Map();
    for (const x of all) {
      if (!x.league_id) continue;
      const k = String(x.league_id);
      const cur = m.get(k) || { id: k, name: x.league_name, country: x.league_country, n: 0 };
      cur.n += 1;
      m.set(k, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.n - a.n).slice(0, 30);
  }, [all]);

  const isUsingFilters = !filtersAreDefault(filters);

  // ── Locked-snapshot machinery (mirrors x5 in Iter 235) ─────────
  const [lockedPicks, setLockedPicks] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setResolved(false);
    (async () => {
      // 1. Try the locked snapshot for the unfiltered default list.
      const existing = await fetchSnapshot(date, "top20");
      if (cancelled) return;
      if (existing?.picks?.length) {
        // Snapshot picks lack the round-of-pick (`_round`) hint, which
        // is purely cosmetic.  Default to 0 (= primary market) — old
        // snapshots will simply not show the R2/R3 chip.
        const hydrated = existing.picks.map((p) => ({ _round: 0, ...p }));
        setLockedPicks(hydrated);
        setResolved(true);
        return;
      }
      // 2. Build once from live data and POST to lock it.
      const fresh = buildTop20(all, DEFAULT_FILTERS);
      if (!fresh.length) {
        setLockedPicks([]);
        setResolved(true);
        return;
      }
      const saved = await postSnapshotAndReturn(date, "top20", fresh);
      if (cancelled) return;
      const finalList = (saved?.picks?.length
        ? saved.picks.map((p) => ({ _round: 0, ...p }))
        : fresh);
      setLockedPicks(finalList);
      setResolved(true);
    })();
    return () => { cancelled = true; };
  }, [date]);

  // What we actually render:
  //   • When filters are active → re-derive from live data (exploratory)
  //   • Otherwise → the locked snapshot, with outcomes refreshed from
  //     the live `/schedule/day` payload so Won/Lost pills stay current
  //     without ever changing pick identity.
  const top = useMemo(() => {
    if (isUsingFilters) return buildTop20(all, filters);
    if (!lockedPicks) return [];
    if (!all.length) return lockedPicks;
    return refreshOutcomesFromLive(lockedPicks, all);
  }, [isUsingFilters, all, filters, lockedPicks]);

  if (!resolved && !isUsingFilters) {
    return (
      <div data-testid="schedule-top20-loading">
        <FilterBar
          allMarketTypes={allMarketTypes}
          allLeagues={allLeagues}
          filters={filters}
          onChange={setFilters}
          onReset={resetFilters}
        />
        <Card className="border-white/10">
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            Loading locked Top 20…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!top.length) {
    return (
      <div>
        <FilterBar
          allMarketTypes={allMarketTypes}
          allLeagues={allLeagues}
          filters={filters}
          onChange={setFilters}
          onReset={resetFilters}
        />
        <Card className="border-white/10" data-testid="schedule-top20-empty">
          <CardContent className="p-6 text-center text-muted-foreground">
            No picks match your filters. Try lowering the confidence threshold.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="schedule-top20-list">
      <PerformancePanel type="top20" days={30} />
      <FilterBar
        allMarketTypes={allMarketTypes}
        allLeagues={allLeagues}
        filters={filters}
        onChange={setFilters}
        onReset={resetFilters}
      />

      <div className="flex items-center justify-between gap-2 flex-wrap mb-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-emerald-300" />
          <span>
            Top <span className="font-bold text-foreground">{top.length}</span> highest-confidence picks — one per fixture, easy-to-predict first.
          </span>
          {!isUsingFilters && lockedPicks?.length ? (
            <Badge
              variant="outline"
              className="text-[9px] py-0 px-1.5 border-cyan-500/40 text-cyan-200 bg-cyan-500/5 font-bold uppercase tracking-wider"
              title="This list was locked the first time it was generated for this day. It will not be reshuffled even when new picks become available or outcomes settle."
              data-testid="schedule-top20-locked-badge"
            >
              Locked
            </Badge>
          ) : null}
          {isUsingFilters ? (
            <Badge
              variant="outline"
              className="text-[9px] py-0 px-1.5 border-amber-500/40 text-amber-200 bg-amber-500/5 font-bold uppercase tracking-wider"
              title="Filtered view — re-derived live from your filters.  The persisted (locked) list is the unfiltered one used for hit-rate tracking."
              data-testid="schedule-top20-filtered-badge"
            >
              Filtered preview
            </Badge>
          ) : null}
        </div>
        <HitMissBar picks={top} testid="top20-hitmiss" />
      </div>

      <div className="space-y-2">
        {top.map((p, i) => {
          const flag = countryToFlag(p.league_country) || "🌍";
          const oc = p.outcome;
          const outcomeTone =
            oc === true
              ? "border-emerald-500/40 bg-emerald-500/[0.04]"
              : oc === false
              ? "border-rose-500/40 bg-rose-500/[0.04]"
              : "border-white/10 bg-white/[0.02]";
          const oc_pill =
            oc === true
              ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`top20-outcome-${i}`}>Won</span>
              : oc === false
              ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`top20-outcome-${i}`}>Lost</span>
              : null;
          return (
            <button
              type="button"
              key={`${p.match_id}-${p.market_type}-${p._round}-${i}`}
              onClick={() => onPickFixture({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
              data-testid={`top20-pick-${i}`}
              className={["w-full text-left rounded-lg border p-3 hover:bg-white/[0.04] transition flex items-center gap-3", outcomeTone].join(" ")}
            >
              <span className="font-mono text-xs text-muted-foreground w-6 shrink-0 text-right">{i + 1}.</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono opacity-70">{fmtKickoff(p.kickoff_iso)}</span>
                  <span className="text-base leading-none">{flag}</span>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground truncate">
                    {p.league_name}{p.league_country ? <span className="text-muted-foreground/60">, {p.league_country}</span> : null}
                  </span>
                  {p._round > 0 && (
                    <span className="text-[9px] uppercase tracking-wider text-cyan-300/80 font-bold" title="Second-best market for this fixture (round 2 pick)">
                      R{p._round + 1}
                    </span>
                  )}
                  {/* Iter 275h — live mini-ticker on every row. */}
                  <LivePickTracker
                    fixtureId={p.fixture_id}
                    matchId={p.match_id}
                    marketType={p.market_type}
                    prediction={p.prediction}
                    testidPrefix="top20-live"
                  />
                </div>
                <div className="text-sm font-semibold mt-0.5 truncate">
                  {p.home} <span className="text-muted-foreground">vs</span> {p.away}
                </div>
                <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">{p.market_type}</span>
                  <span className="text-foreground font-medium">→ {p.prediction}</span>
                  {oc_pill}
                </div>
              </div>
              <Badge
                variant="outline"
                className={["text-xs py-0 px-2 shrink-0 font-bold", RISK_COLORS[p.risk_level] || ""].join(" ")}
                data-testid={`top20-pick-confidence-${i}`}
              >
                {Math.round(p.confidence)}%
              </Badge>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// x5 Odds Builder  ============================================================
// =============================================================================

const X5_MARKETS = new Set(["Double Chance", "HT BTTS", "HT O/U 0.5"]);
const TARGET_PARLAY_ODDS = 5.0;
const MAX_LEGS = 25;

const buildX5Parlay = (allMarkets) => {
  const pool = allMarkets
    // Iter 275 — early-stage Betway league gate: keep only fixtures
    // whose league is available on Betway so the accumulator is
    // actually placeable.  Runs BEFORE any ranking / selection so
    // the prediction, confidence and odds logic downstream is
    // untouched.
    .filter((m) => m.betway_eligible !== false)
    .filter((m) => X5_MARKETS.has(m.market_type))
    .filter((m) => m.outcome !== false)
    .sort((a, b) => (b.confidence || 0) - (a.confidence || 0));

  const used = new Set();
  const legs = [];
  let product = 1.0;

  for (const m of pool) {
    if (legs.length >= MAX_LEGS) break;
    if (used.has(m.match_id)) continue;
    const o = impliedOdds(m.confidence);
    used.add(m.match_id);
    legs.push({ ...m, implied_odds: o });
    product *= o;
    if (product >= TARGET_PARLAY_ODDS) break;
  }
  return { legs, product };
};

// Iter 235 — locked-list snapshot:
// Once an x5 list is produced for a given day, freeze it.  Subsequent
// renders / reloads must NOT re-shuffle even when new fixtures arrive
// or outcomes settle — otherwise hit-rate tracking is meaningless
// (the algorithm would silently rewrite history).
//
// Strategy:
//   1. On mount, ask the server for the existing snapshot for this date.
//   2. If a snapshot exists → render its legs (locked).
//   3. If none exists → run `buildX5Parlay(allLive)`, POST the snapshot,
//      then render those legs.  The backend POST is idempotent (unique
//      index on (date, type, list_key)), so racing tabs land on the
//      same locked list.
//   4. Always re-stamp the locked legs' `outcome` field from the live
//      `/schedule/day` payload so the Won / Lost pills + hit-rate bar
//      stay current — without ever changing which picks are in the list.

const fetchSnapshot = async (date, type) => {
  try {
    const r = await fetch(`${API_URL}/api/picks/snapshot/${type}/${date}?list_key=default`);
    if (!r.ok) return null;
    const j = await r.json();
    return j.snapshot || null;
  } catch { return null; }
};

const postSnapshotAndReturn = async (date, type, picks) => {
  try {
    const r = await fetch(`${API_URL}/api/picks/snapshot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date, type, list_key: "default",
        picks: picks.map((p) => ({
          match_id: String(p.match_id || ""),
          league_id: p.league_id,
          league_name: p.league_name || "",
          league_country: p.league_country || "",
          home: p.home || "",
          away: p.away || "",
          kickoff_iso: p.kickoff_iso,
          market_type: p.market_type || "",
          prediction: p.prediction || "",
          confidence: p.confidence || 0,
          risk_level: p.risk_level || null,
          outcome: p.outcome != null ? Boolean(p.outcome) : null,
        })),
      }),
    });
    if (!r.ok) return null;
    const j = await r.json();
    return j.snapshot || null;
  } catch { return null; }
};

// Re-stamp `outcome` on each snapshot pick from the live data — used
// to keep Won/Lost pills accurate without ever changing pick identity.
const refreshOutcomesFromLive = (snapshotPicks, liveMarkets) => {
  const liveByKey = new Map();
  for (const m of liveMarkets) {
    const k = `${m.match_id}|${m.market_type}|${m.prediction}`;
    liveByKey.set(k, m.outcome);
  }
  return snapshotPicks.map((p) => {
    const k = `${p.match_id}|${p.market_type}|${p.prediction}`;
    const live = liveByKey.get(k);
    return live === undefined ? p : { ...p, outcome: live };
  });
};

export const X5OddsBuilder = ({ data, date, onPickFixture, betwayConnected = false }) => {
  const all = useMemo(() => flattenMarkets(data), [data]);
  // `lockedLegs` is the canonical list — once set for a (date, x5)
  // tuple it never changes until the user moves to a different day.
  const [lockedLegs, setLockedLegs] = useState(null);
  const [resolved, setResolved] = useState(false); // true once load-or-build settles

  // Load or create the locked snapshot whenever the day changes.
  // Deliberately depends on `date` only, NOT on `all` / `data`, so
  // intra-day re-renders (outcomes flipping, fixtures appearing late)
  // do NOT re-trigger a rebuild.
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setResolved(false);
    (async () => {
      // 1. Try to load the locked snapshot.
      const existing = await fetchSnapshot(date, "x5");
      if (cancelled) return;
      if (existing?.picks?.length) {
        // Hydrate decimal-odds field which the server doesn't store.
        const legs = existing.picks.map((p) => ({
          ...p,
          implied_odds: impliedOdds(p.confidence),
        }));
        setLockedLegs(legs);
        setResolved(true);
        return;
      }
      // 2. No locked snapshot yet → compute from live data once and POST.
      const fresh = buildX5Parlay(all);
      if (!fresh.legs.length) {
        setLockedLegs([]);
        setResolved(true);
        return;
      }
      const saved = await postSnapshotAndReturn(date, "x5", fresh.legs);
      if (cancelled) return;
      // Prefer the server's view (idempotent: another tab may have
      // raced us and locked a different list — defer to it).
      const finalLegs = (saved?.picks ? saved.picks : fresh.legs).map((p) => ({
        ...p,
        implied_odds: impliedOdds(p.confidence),
      }));
      setLockedLegs(finalLegs);
      setResolved(true);
    })();
    return () => { cancelled = true; };
  }, [date]);

  // Re-stamp outcomes from live data on every render — UI shows fresh
  // Won/Lost pills, but the *identity* of the legs stays locked.
  const legs = useMemo(() => {
    if (!lockedLegs) return [];
    if (!all.length) return lockedLegs;
    return refreshOutcomesFromLive(lockedLegs, all);
  }, [lockedLegs, all]);

  const product = useMemo(
    () => legs.reduce((acc, p) => acc * impliedOdds(p.confidence), 1.0),
    [legs],
  );

  if (!resolved) {
    // Snapshot fetch in flight — render a quiet skeleton instead of
    // flashing the "no picks" empty state for ~200ms.
    return (
      <div data-testid="schedule-x5-loading">
        <PerformancePanel type="x5" days={30} />
        <Card className="border-white/10">
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            Loading locked accumulator…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!legs.length) {
    return (
      <div>
        <PerformancePanel type="x5" days={30} />
        <Card className="border-white/10" data-testid="schedule-x5-empty">
          <CardContent className="p-6 text-center text-muted-foreground">
            Not enough Double-Chance / Halftime picks for this day to build a 5x parlay.
          </CardContent>
        </Card>
      </div>
    );
  }

  const stake = 5;
  const potentialReturn = (stake * product).toFixed(2);
  const hitTarget = product >= TARGET_PARLAY_ODDS;
  const summaryTone = hitTarget
    ? "border-emerald-500/30 bg-emerald-500/[0.04]"
    : "border-amber-500/30 bg-amber-500/[0.04]";

  return (
    <div data-testid="schedule-x5-builder" className="space-y-3">
      <PerformancePanel type="x5" days={30} />
      <Card className={summaryTone} data-testid="schedule-x5-summary">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Calculator className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold tracking-tight flex items-center gap-2 flex-wrap">
                5x Odds Accumulator · {legs.length}-leg
                <Badge
                  variant="outline"
                  className="text-[9px] py-0 px-1.5 border-cyan-500/40 text-cyan-200 bg-cyan-500/5 font-bold uppercase tracking-wider"
                  title="This list was locked on the date this day's snapshot was first generated. It will not be reshuffled even if new picks become available later."
                  data-testid="schedule-x5-locked-badge"
                >
                  Locked
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Highest-confidence Double Chance + Halftime picks of the day, one leg per fixture, stacked until the implied odds reach 5x.
              </div>
              <div className="mt-3 flex items-center gap-4 flex-wrap font-tabular">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">Implied odds</div>
                  <div
                    className={["text-xl font-black", hitTarget ? "text-emerald-300" : "text-amber-300"].join(" ")}
                    data-testid="schedule-x5-product"
                  >
                    {product.toFixed(2)}x
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">$5 stake returns</div>
                  <div className="text-xl font-black text-foreground" data-testid="schedule-x5-return">
                    ${potentialReturn}
                  </div>
                </div>
                <HitMissBar picks={legs} testid="x5-hitmiss" />
                {!hitTarget && (
                  <div className="text-[11px] text-amber-300/90 max-w-[220px]">
                    Below 5x target — not enough qualifying DC/HT picks today.
                  </div>
                )}
              </div>
            </div>
            {betwayConnected && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => { window.location.href = "/account"; }}
                data-testid="schedule-x5-place-btn"
              >
                Place on Betway
                <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-1 mt-2">
        Legs
      </div>
      {legs.map((p, i) => {
        const flag = countryToFlag(p.league_country) || "🌍";
        const oc = p.outcome;
        const outcomeTone =
          oc === true ? "border-emerald-500/40 bg-emerald-500/[0.04]"
          : oc === false ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/10 bg-white/[0.02]";
        const oc_pill =
          oc === true ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`x5-leg-outcome-${i}`}>Won</span>
          : oc === false ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`x5-leg-outcome-${i}`}>Lost</span>
          : null;
        return (
          <button
            type="button"
            key={`${p.match_id}-${i}`}
            onClick={() => onPickFixture({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
            data-testid={`x5-leg-${i}`}
            className={["w-full text-left rounded-lg border p-3 hover:bg-white/[0.04] transition flex items-center gap-3", outcomeTone].join(" ")}
          >
            <span className="font-mono text-xs text-emerald-300 w-6 shrink-0 text-right font-bold">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono opacity-70">{fmtKickoff(p.kickoff_iso)}</span>
                <span className="text-base leading-none">{flag}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground truncate">
                    {p.league_name}{p.league_country ? <span className="text-muted-foreground/60">, {p.league_country}</span> : null}
                  </span>
                {/* Iter 275h — live mini-ticker on every x5 leg. */}
                <LivePickTracker
                  fixtureId={p.fixture_id}
                  matchId={p.match_id}
                  marketType={p.market_type}
                  prediction={p.prediction}
                  testidPrefix="x5-live"
                />
              </div>
              <div className="text-sm font-semibold mt-0.5 truncate">
                {p.home} <span className="text-muted-foreground">vs</span> {p.away}
              </div>
              <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-cyan-500/30 text-cyan-300 bg-cyan-500/5">
                  {p.market_type}
                </Badge>
                <span className="text-foreground font-medium">→ {p.prediction}</span>
                {oc_pill}
              </div>
            </div>
            <div className="text-right shrink-0">
              <Badge
                variant="outline"
                className={["text-[10px] py-0 px-1.5 font-bold", RISK_COLORS[p.risk_level] || ""].join(" ")}
                data-testid={`x5-leg-confidence-${i}`}
              >
                {Math.round(p.confidence)}%
              </Badge>
              <div
                className="text-xs font-tabular font-bold text-foreground mt-1"
                data-testid={`x5-leg-odds-${i}`}
                title="Implied decimal odds (100 / confidence)"
              >
                @{p.implied_odds.toFixed(2)}
              </div>
            </div>
          </button>
        );
      })}

      <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5">
        <TrendingUp className="w-3 h-3" />
        Implied odds derived from our internal confidence model (100 ÷ confidence%). Actual Betway odds may differ.
      </div>
    </div>
  );
};


// =============================================================================
// Easy Wins  — Top 20 easiest-to-predict HOME WIN bets ========================
// =============================================================================
//
// A "home win" pick is one that pays out when the home team wins:
//   • 1X2 Match Result    + prediction === "Home Win"      (highest payout)
//   • Draw No Bet (DNB)   + prediction starts with "Home Win" (refunds on draw)
//
// "Easy" = high confidence + non-High risk.  We tighten the band to
// confidence ≥ 75% AND risk_level ≠ "High".  One pick per fixture;
// when a fixture has BOTH a 1X2 Home Win AND a DNB Home Win, we
// prefer 1X2 (more upside).  Locked-snapshot semantics identical to
// Top 20 (Iter 236) and x5 (Iter 235).

const EASY_WIN_MIN_CONFIDENCE = 75;

const isHomeWinPick = (m) => {
  const mt = m.market_type;
  const pred = (m.prediction || "").toLowerCase();
  if (mt === "1X2 Match Result") return pred === "home win";
  if (mt === "Draw No Bet (DNB)") return pred.startsWith("home win");
  return false;
};

const buildEasyWins = (allMarkets) => {
  // 1. Pool: home-win picks meeting the easy band.
  const pool = allMarkets
    // Iter 275c — early-stage Betway league gate (same rule as x5 and
    // Top 20): Easy Wins must all be placeable on Betway.
    .filter((m) => m.betway_eligible !== false)
    .filter(isHomeWinPick)
    .filter((m) => (m.confidence || 0) >= EASY_WIN_MIN_CONFIDENCE)
    .filter((m) => m.risk_level !== "High");

  // 2. Group by match — prefer 1X2 over DNB for the same fixture (1X2
  //    pays more, same outcome trigger).  Within market type, pick the
  //    highest-easyScore market (handles future expansions).
  const byMatch = new Map();
  for (const m of pool) {
    const cur = byMatch.get(m.match_id);
    if (!cur) { byMatch.set(m.match_id, m); continue; }
    const curIsX2 = cur.market_type === "1X2 Match Result";
    const newIsX2 = m.market_type === "1X2 Match Result";
    if (newIsX2 && !curIsX2) { byMatch.set(m.match_id, m); continue; }
    if (curIsX2 && !newIsX2) continue;
    // Same market type → keep higher easyScore.
    if (easyScore(m) > easyScore(cur)) byMatch.set(m.match_id, m);
  }

  // 3. Sort across fixtures by easyScore DESC, take 20.
  return Array.from(byMatch.values())
    .sort((a, b) => easyScore(b) - easyScore(a))
    .slice(0, 20);
};

export const EasyWinsList = ({ data, date, onPickFixture }) => {
  const all = useMemo(() => flattenMarkets(data), [data]);
  const [lockedPicks, setLockedPicks] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setResolved(false);
    (async () => {
      const existing = await fetchSnapshot(date, "easywin");
      if (cancelled) return;
      if (existing?.picks?.length) {
        setLockedPicks(existing.picks);
        setResolved(true);
        return;
      }
      const fresh = buildEasyWins(all);
      if (!fresh.length) { setLockedPicks([]); setResolved(true); return; }
      const saved = await postSnapshotAndReturn(date, "easywin", fresh);
      if (cancelled) return;
      setLockedPicks(saved?.picks?.length ? saved.picks : fresh);
      setResolved(true);
    })();
    return () => { cancelled = true; };
  }, [date]);

  const picks = useMemo(() => {
    if (!lockedPicks) return [];
    if (!all.length) return lockedPicks;
    return refreshOutcomesFromLive(lockedPicks, all);
  }, [lockedPicks, all]);

  if (!resolved) {
    return (
      <div data-testid="schedule-easywin-loading">
        <PerformancePanel type="easywin" days={30} />
        <Card className="border-white/10">
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            Loading locked Easy Wins…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!picks.length) {
    return (
      <div>
        <PerformancePanel type="easywin" days={30} />
        <Card className="border-white/10" data-testid="schedule-easywin-empty">
          <CardContent className="p-6 text-center text-muted-foreground">
            No easy home-win picks for this day. (Need confidence ≥ {EASY_WIN_MIN_CONFIDENCE}% and non-High risk.)
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="schedule-easywin-list">
      <PerformancePanel type="easywin" days={30} />

      <div className="flex items-center justify-between gap-2 flex-wrap mb-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 flex-wrap">
          <Trophy className="w-4 h-4 text-emerald-300" />
          <span>
            Top <span className="font-bold text-foreground">{picks.length}</span> easy-to-predict home-win bets (1X2 / DNB, ≥{EASY_WIN_MIN_CONFIDENCE}%, low-medium risk).
          </span>
          <Badge
            variant="outline"
            className="text-[9px] py-0 px-1.5 border-cyan-500/40 text-cyan-200 bg-cyan-500/5 font-bold uppercase tracking-wider"
            title="This list was locked the first time it was generated for this day. It will not be reshuffled."
            data-testid="schedule-easywin-locked-badge"
          >
            Locked
          </Badge>
        </div>
        <HitMissBar picks={picks} testid="easywin-hitmiss" />
      </div>

      <div className="space-y-2">
        {picks.map((p, i) => {
          const flag = countryToFlag(p.league_country) || "🌍";
          const oc = p.outcome;
          const outcomeTone =
            oc === true
              ? "border-emerald-500/40 bg-emerald-500/[0.04]"
              : oc === false
              ? "border-rose-500/40 bg-rose-500/[0.04]"
              : "border-white/10 bg-white/[0.02]";
          const oc_pill =
            oc === true
              ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`easywin-outcome-${i}`}>Won</span>
              : oc === false
              ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`easywin-outcome-${i}`}>Lost</span>
              : null;
          return (
            <button
              type="button"
              key={`${p.match_id}-${i}`}
              onClick={() => onPickFixture({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
              data-testid={`easywin-pick-${i}`}
              className={["w-full text-left rounded-lg border p-3 hover:bg-white/[0.04] transition flex items-center gap-3", outcomeTone].join(" ")}
            >
              <span className="font-mono text-xs text-muted-foreground w-6 shrink-0 text-right">{i + 1}.</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono opacity-70">{fmtKickoff(p.kickoff_iso)}</span>
                  <span className="text-base leading-none">{flag}</span>
                  <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground truncate">
                    {p.league_name}{p.league_country ? <span className="text-muted-foreground/60">, {p.league_country}</span> : null}
                  </span>
                  {/* Iter 275h — live mini-ticker on every Easy Wins row. */}
                  <LivePickTracker
                    fixtureId={p.fixture_id}
                    matchId={p.match_id}
                    marketType={p.market_type}
                    prediction={p.prediction}
                    testidPrefix="easywin-live"
                  />
                </div>
                <div className="text-sm font-semibold mt-0.5 truncate">
                  <span className="text-emerald-300">{p.home}</span> <span className="text-muted-foreground">vs</span> {p.away}
                </div>
                <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-500/30 text-emerald-200 bg-emerald-500/5">
                    {p.market_type === "1X2 Match Result" ? "1X2" : "DNB"}
                  </Badge>
                  <span className="text-foreground font-medium">→ {p.prediction}</span>
                  {oc_pill}
                </div>
              </div>
              <Badge
                variant="outline"
                className={["text-xs py-0 px-2 shrink-0 font-bold", RISK_COLORS[p.risk_level] || ""].join(" ")}
                data-testid={`easywin-pick-confidence-${i}`}
              >
                {Math.round(p.confidence)}%
              </Badge>
            </button>
          );
        })}
      </div>
    </div>
  );
};



// =============================================================================
// Top 20 Elite (Iter 63) — historical-market-driven selector ==================
// =============================================================================
// Fetches the backend `/schedule/top20-elite` endpoint which enforces the
// 16-rule elite specification: hard confidence gates, tier classification
// (ELITE / VERY_STRONG / STRONG) against historical (league × market ×
// prediction) profiles with statistical shrinkage, recency downgrade,
// contradiction checks, 1-per-fixture diversification, and — critically —
// is willing to return fewer than 20 picks when the corpus doesn't support
// more.  Quality > quantity.
//
// The endpoint returns:
//   { count, cap, criteria_version, profile_count, candidates_considered,
//     tier_counts: { ELITE, VERY_STRONG, STRONG }, picks: [{...}] }

const TIER_STYLES = {
  A: "bg-amber-500/25 text-amber-100 border-amber-400/60",
  B: "bg-violet-500/20 text-violet-100 border-violet-400/60",
  C: "bg-sky-500/20 text-sky-100 border-sky-400/60",
  D: "bg-slate-500/20 text-slate-100 border-slate-400/40",
};

const TIER_LABELS = {
  A: "TIER A",
  B: "TIER B",
  C: "TIER C",
  D: "TIER D",
};

export const Top20EliteList = ({ date, onPickFixture }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outcomeMap, setOutcomeMap] = useState({}); // key → true|false|null

  // Merge server-settled outcomes onto the fresh elite payload so the
  // HitMissBar can score today's picks the moment the settler flips them.
  const picksWithOutcomes = useMemo(() => {
    const arr = payload?.picks || [];
    if (!Object.keys(outcomeMap).length) return arr;
    return arr.map((p) => {
      const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
      const settled = outcomeMap[key];
      return settled === undefined ? p : { ...p, outcome: settled };
    });
  }, [payload, outcomeMap]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOutcomeMap({});

    (async () => {
      try {
        // 1. Fetch today's fresh elite selection (metadata + composite score).
        const res = await fetch(`${API_URL}/api/schedule/top20-elite?date=${date}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (cancelled) return;
        setPayload(j);

        // 2. Snapshot the fresh picks idempotently for hit-tracking.
        snapshotPicks(date, "top20elite", j.picks || []);

        // 3. Pull the SERVER snapshot so we get any outcomes already
        //    stamped by the picks_snapshot_settler cron.
        try {
          const snapRes = await fetch(`${API_URL}/api/picks/snapshot/top20elite/${date}?list_key=default`);
          if (snapRes.ok && !cancelled) {
            const snap = (await snapRes.json())?.snapshot;
            if (snap?.picks?.length) {
              const map = {};
              for (const p of snap.picks) {
                const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
                map[key] = p.outcome === true ? true : p.outcome === false ? false : null;
              }
              setOutcomeMap(map);
            }
          }
        } catch { /* snapshot fetch is best-effort */ }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="top20elite-loading">
        Evaluating today's markets against historical profiles…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-rose-300" data-testid="top20elite-error">
        Elite selector unavailable ({error}) — retry in a moment.
      </div>
    );
  }
  const picks = picksWithOutcomes;
  const tierCounts = payload?.tier_counts || {};

  return (
    <div className="space-y-3" data-testid="top20elite-container">
      {/* Rolling 30-day performance panel — same as Top 20 Picks */}
      <PerformancePanel type="top20elite" days={30} />

      {/* Header / diagnostics */}
      <Card className="border-amber-500/20 bg-amber-500/[0.03]">
        <CardContent className="p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-300" />
            <div>
              <div className="text-sm font-semibold text-amber-100">
                {picks.length} elite pick{picks.length === 1 ? "" : "s"}
                <span className="text-muted-foreground font-normal"> · target 20</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Historical market intelligence × current-match evidence · graceful tier degradation · goal is 20 safest opportunities, not 20 highest confidences.
              </div>
              {payload?.corpus_total > 0 && (
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Trained on <span className="font-semibold text-amber-200/70">{payload.corpus_total.toLocaleString()} all-time historical picks</span>
                  {" "}across <span className="font-semibold text-amber-200/70">{payload.profile_count?.toLocaleString()}</span> league × market × prediction profiles.
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <HitMissBar picks={picks} testid="top20elite-hitmiss" />
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              <Badge className={TIER_STYLES.A}>Tier A · {tierCounts.A || 0}</Badge>
              <Badge className={TIER_STYLES.B}>Tier B · {tierCounts.B || 0}</Badge>
              <Badge className={TIER_STYLES.C}>Tier C · {tierCounts.C || 0}</Badge>
              <Badge className={TIER_STYLES.D}>Tier D · {tierCounts.D || 0}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {picks.length === 0 && (
        <Card className="border-white/10">
          <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="top20elite-empty">
            No candidate cleared the elite criteria for {date}. We considered
            {" "}{payload?.candidates_considered ?? 0} markets against
            {" "}{payload?.profile_count ?? 0} historical league × market × prediction profiles.
            Quality-first — check back when the schedule matures, or try tomorrow.
          </CardContent>
        </Card>
      )}

      {/* Pick rows */}
      {picks.map((p, i) => {
        const flag = countryToFlag(p.league_country) || "🌍";
        const oc = p.outcome;
        const outcomeTone =
          oc === true  ? "border-emerald-500/40 bg-emerald-500/[0.04]"
          : oc === false ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/10";
        const oc_pill =
          oc === true  ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`top20elite-outcome-${i}`}>Won</span>
          : oc === false ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`top20elite-outcome-${i}`}>Lost</span>
          : null;
        return (
          <button
            key={`${p.match_id}-${p.market_type}-${i}`}
            type="button"
            onClick={() => onPickFixture?.({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
            data-testid={`top20elite-pick-${i}`}
            className="w-full text-left group"
          >
            <Card className={["hover:border-amber-500/30 transition", outcomeTone].join(" ")}>
              <CardContent className="p-4 space-y-2">
                {/* Row 1 — tier + market + confidence */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground w-6 tabular-nums">#{i + 1}</span>
                  <Badge className={["text-[10px] uppercase tracking-wide font-bold border", TIER_STYLES[p.tier] || ""].join(" ")}>
                    {TIER_LABELS[p.tier] || p.tier}
                  </Badge>
                  <span className="text-sm font-semibold text-white">
                    {p.market_type} <span className="text-emerald-300">→ {p.prediction}</span>
                  </span>
                  {/* Live indicators — same LivePickTracker used by Top 20 Picks / x5 / Easy Wins. */}
                  <LivePickTracker
                    fixtureId={p.fixture_id}
                    matchId={p.match_id}
                    marketType={p.market_type}
                    prediction={p.prediction}
                    testidPrefix="top20elite-live"
                  />
                  <span className="ml-auto flex items-center gap-2">
                    {oc_pill}
                    <Badge className={RISK_COLORS[p.risk_level] || ""}>
                      {p.risk_level}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-bold border-white/20">
                      {Math.round(p.confidence)}%
                    </Badge>
                  </span>
                </div>

                {/* Row 2 — fixture + kickoff */}
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-base leading-none">{flag}</span>
                  <span className="text-muted-foreground truncate">{p.league_name}</span>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="text-white truncate font-medium">{p.home} <span className="text-muted-foreground">vs</span> {p.away}</span>
                  {p.kickoff_iso && <span className="text-muted-foreground ml-auto">{fmtKickoff(p.kickoff_iso)}</span>}
                </div>

                {/* Row 3 — historical corpus (all-time) */}
                <div className="flex items-center gap-3 text-[11px] flex-wrap pt-1 border-t border-white/[0.06]">
                  <span className="text-amber-200/90">
                    All-time <span className="font-bold">{p.historical_accuracy}%</span>
                    <span className="text-muted-foreground/70"> · n={p.sample_size}</span>
                  </span>
                  {p.recent_20_accuracy != null && (
                    <span className="text-violet-200/90">
                      Recent 20 <span className="font-bold">{p.recent_20_accuracy}%</span>
                    </span>
                  )}
                  {p.recent_10_accuracy != null && (
                    <span className="text-sky-200/90">
                      Recent 10 <span className="font-bold">{p.recent_10_accuracy}%</span>
                    </span>
                  )}
                  <span className="ml-auto text-white/80 text-xs">
                    Q-score <span className="font-bold">{p.quality_score}</span>
                  </span>
                </div>

                {p.red_flags?.length > 0 && (
                  <div className="text-[11px] text-amber-300/80">
                    ⚠ {p.red_flags.join(" · ")}
                  </div>
                )}
              </CardContent>
            </Card>
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// Top 20 MASTER PICKS (Iter 79) ================================================
// =============================================================================
// Weekly (Mon → Sun) selection restricted to approved major European leagues.
// Same fetch → snapshot → merge outcomes architecture as `Top20EliteList` so
// the hit tracker, live indicators and outcome badges work identically.
// Backend enforces Tier B floor + per-league cap (max 6/league) + every
// hardening rule from Iter 66/72/73/75/77.

const MASTER_TIER_STYLES = {
  "A+": "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  "A":  "bg-amber-500/15 text-amber-200 border-amber-500/40",
  "B":  "bg-sky-500/15 text-sky-200 border-sky-500/40",
};

// Snap the date (default: today in the app timezone) back to Monday for the default week param.
const _weekStartFor = (date) => {
  try {
    return weekStartIso(date || undefined);
  } catch { return null; }
};

export const Top20MasterList = ({ date, onPickFixture }) => {
  const weekStart = _weekStartFor(date);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outcomeMap, setOutcomeMap] = useState({});

  const picksWithOutcomes = useMemo(() => {
    const arr = payload?.picks || [];
    if (!Object.keys(outcomeMap).length) return arr;
    return arr.map((p) => {
      const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
      const settled = outcomeMap[key];
      return settled === undefined ? p : { ...p, outcome: settled };
    });
  }, [payload, outcomeMap]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOutcomeMap({});

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/schedule/top20-master?week_start=${weekStart}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (cancelled) return;
        setPayload(j);
        snapshotPicks(weekStart, "top20master", j.picks || []);
        try {
          const snapRes = await fetch(`${API_URL}/api/picks/snapshot/top20master/${weekStart}?list_key=default`);
          if (snapRes.ok && !cancelled) {
            const snap = (await snapRes.json())?.snapshot;
            if (snap?.picks?.length) {
              const map = {};
              for (const p of snap.picks) {
                const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
                map[key] = p.outcome === true ? true : p.outcome === false ? false : null;
              }
              setOutcomeMap(map);
            }
          }
        } catch { /* best-effort */ }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [weekStart]);

  if (loading) {
    return <div className="text-sm text-muted-foreground" data-testid="top20master-loading">Scanning the week's approved leagues…</div>;
  }
  if (error) {
    return <div className="text-sm text-rose-300" data-testid="top20master-error">Master selector unavailable ({error}) — retry in a moment.</div>;
  }
  const picks = picksWithOutcomes;
  const tierCounts = payload?.tier_counts || {};
  const leagueDist = payload?.league_distribution || {};

  return (
    <div className="space-y-3" data-testid="top20master-container">
      <PerformancePanel type="top20master" days={30} />

      <Card className="border-emerald-500/20 bg-emerald-500/[0.03]">
        <CardContent className="p-4 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-2">
            <Trophy className="w-5 h-5 text-emerald-300 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-emerald-100">
                {picks.length} master pick{picks.length === 1 ? "" : "s"}
                <span className="text-muted-foreground font-normal"> · target 20 · week of {weekStart}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Approved major European leagues only · Tier B floor (≥80/100) · max 6 per league · quality-first (may return fewer than 20 by design).
              </div>
              {payload?.corpus_total > 0 && (
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Backed by <span className="font-semibold text-emerald-200/70">{payload.corpus_total.toLocaleString()} all-time historical picks</span>
                  {" "}across <span className="font-semibold text-emerald-200/70">{payload.profile_count?.toLocaleString()}</span> profiles.
                </div>
              )}
              {Object.keys(leagueDist).length > 0 && (
                <div className="text-[10px] text-muted-foreground/70 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                  {Object.entries(leagueDist).sort((a,b) => b[1]-a[1]).map(([lg, n]) => (
                    <span key={lg}><span className="font-semibold text-emerald-200/60">{lg}</span> · {n}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <HitMissBar picks={picks} testid="top20master-hitmiss" />
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              <Badge className={MASTER_TIER_STYLES["A+"]}>A+ · {tierCounts["A+"] || 0}</Badge>
              <Badge className={MASTER_TIER_STYLES["A"]}>A · {tierCounts["A"] || 0}</Badge>
              <Badge className={MASTER_TIER_STYLES["B"]}>B · {tierCounts["B"] || 0}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {picks.length === 0 && (
        <Card className="border-white/10">
          <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="top20master-empty">
            No fixture from the approved European majors cleared the master threshold for the week of {weekStart}.
            {" "}Considered {payload?.candidates_considered ?? 0} markets after filtering.
            Quality-first — the engine refuses to fill the slate with weak picks.
          </CardContent>
        </Card>
      )}

      {picks.map((p, i) => {
        const flag = countryToFlag(p.league_country) || "🌍";
        const oc = p.outcome;
        const outcomeTone =
          oc === true  ? "border-emerald-500/40 bg-emerald-500/[0.04]"
          : oc === false ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/10";
        const oc_pill =
          oc === true  ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`top20master-outcome-${i}`}>Won</span>
          : oc === false ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`top20master-outcome-${i}`}>Lost</span>
          : null;
        // Iter 79 — "Why this pick?" hover tooltip built from the pick's
        // own diagnostics.  Kept concise so it fits on hover without a
        // modal — full analysis lives on the dossier click-through.
        const evidence = [];
        if (p.historical_accuracy != null) evidence.push(`All-time ${p.historical_accuracy}% (n=${p.sample_size})`);
        if (p.recent_20_accuracy != null) evidence.push(`Recent 20 ${p.recent_20_accuracy}%`);
        if (p.recent_10_accuracy != null) evidence.push(`Recent 10 ${p.recent_10_accuracy}%`);
        if (p.confidence != null) evidence.push(`Model conf ${Math.round(p.confidence)}%`);
        const whyTip = [
          `WHY THIS PICK?`,
          ``,
          `Market: ${p.market_type} → ${p.prediction}`,
          `Predictability: ${p.quality_score}/100 (${p.tier_label || p.tier})`,
          ``,
          `KEY EVIDENCE`,
          ...evidence.map((e) => `• ${e}`),
          p.rationale ? `` : null,
          p.rationale ? `RATIONALE` : null,
          p.rationale || null,
          p.red_flags?.length ? `` : null,
          p.red_flags?.length ? `MAIN RISK · ${p.red_flags.join(" · ")}` : null,
        ].filter(Boolean).join("\n");
        return (
          <button
            key={`${p.match_id}-${p.market_type}-${i}`}
            type="button"
            title={whyTip}
            onClick={() => onPickFixture?.({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
            data-testid={`top20master-pick-${i}`}
            className="w-full text-left group"
          >
            <Card className={["hover:border-emerald-500/30 transition", outcomeTone].join(" ")}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground w-6 tabular-nums">#{i + 1}</span>
                  <Badge className={["text-[10px] uppercase tracking-wide font-bold border", MASTER_TIER_STYLES[p.tier_label] || MASTER_TIER_STYLES.B].join(" ")}>
                    TIER {p.tier_label || "B"}
                  </Badge>
                  <span className="text-sm font-semibold text-white">
                    {p.market_type} <span className="text-emerald-300">→ {p.prediction}</span>
                  </span>
                  <LivePickTracker
                    fixtureId={p.fixture_id}
                    matchId={p.match_id}
                    marketType={p.market_type}
                    prediction={p.prediction}
                    testidPrefix="top20master-live"
                  />
                  <span className="ml-auto flex items-center gap-2">
                    {oc_pill}
                    <Badge className={RISK_COLORS[p.risk_level] || ""}>{p.risk_level}</Badge>
                    <Badge variant="outline" className="text-xs font-bold border-white/20">
                      {Math.round(p.confidence)}%
                    </Badge>
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-base leading-none">{flag}</span>
                  <span className="text-muted-foreground truncate">{p.league_name}</span>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="text-white truncate font-medium">{p.home} <span className="text-muted-foreground">vs</span> {p.away}</span>
                  {p.kickoff_iso && <span className="text-muted-foreground ml-auto">{fmtKickoff(p.kickoff_iso)}</span>}
                </div>

                <div className="flex items-center gap-3 text-[11px] flex-wrap pt-1 border-t border-white/[0.06]">
                  <span className="text-emerald-200/90">
                    All-time <span className="font-bold">{p.historical_accuracy}%</span>
                    <span className="text-muted-foreground/70"> · n={p.sample_size}</span>
                  </span>
                  {p.recent_20_accuracy != null && (
                    <span className="text-violet-200/90">Recent 20 <span className="font-bold">{p.recent_20_accuracy}%</span></span>
                  )}
                  {p.recent_10_accuracy != null && (
                    <span className="text-sky-200/90">Recent 10 <span className="font-bold">{p.recent_10_accuracy}%</span></span>
                  )}
                  <span className="ml-auto text-white/80 text-xs">
                    Predictability <span className="font-bold">{p.quality_score}</span>/100
                  </span>
                </div>

                {p.red_flags?.length > 0 && (
                  <div className="text-[11px] text-amber-300/80">⚠ {p.red_flags.join(" · ")}</div>
                )}
              </CardContent>
            </Card>
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// x5 Elite Builder (Iter 69) — Elite-scoring DC + HT accumulator ==============
// =============================================================================
// Same architecture as `Top20EliteList` (fetch → snapshot → merge outcomes) but
// the backend selector restricts to Double Chance + HT BTTS + HT O/U 0.5 and
// stops accumulating legs the moment cumulative implied odds >= 5.0x.

export const X5EliteBuilder = ({ date, onPickFixture }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outcomeMap, setOutcomeMap] = useState({});

  const legsWithOutcomes = useMemo(() => {
    const arr = payload?.picks || [];
    if (!Object.keys(outcomeMap).length) return arr;
    return arr.map((p) => {
      const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
      const settled = outcomeMap[key];
      return settled === undefined ? p : { ...p, outcome: settled };
    });
  }, [payload, outcomeMap]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOutcomeMap({});

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/schedule/x5-elite?date=${date}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (cancelled) return;
        setPayload(j);
        snapshotPicks(date, "x5elite", j.picks || []);
        try {
          const snapRes = await fetch(`${API_URL}/api/picks/snapshot/x5elite/${date}?list_key=default`);
          if (snapRes.ok && !cancelled) {
            const snap = (await snapRes.json())?.snapshot;
            if (snap?.picks?.length) {
              const map = {};
              for (const p of snap.picks) {
                const key = `${p.match_id}||${p.market_type}||${(p.prediction || "").toLowerCase()}`;
                map[key] = p.outcome === true ? true : p.outcome === false ? false : null;
              }
              setOutcomeMap(map);
            }
          }
        } catch { /* best-effort */ }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="x5elite-loading">
        Evaluating DC + HT markets against historical profiles…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-rose-300" data-testid="x5elite-error">
        x5 Elite unavailable ({error}) — retry in a moment.
      </div>
    );
  }
  const legs = legsWithOutcomes;
  const tierCounts = payload?.tier_counts || {};
  const product = payload?.product || 1;
  const hitTarget = payload?.hit_target;
  const target = payload?.target_product || 5;

  return (
    <div className="space-y-3" data-testid="x5elite-container">
      <PerformancePanel type="x5elite" days={30} />

      {/* Header */}
      <Card className="border-amber-500/20 bg-amber-500/[0.03]">
        <CardContent className="p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-300" />
            <div>
              <div className="text-sm font-semibold text-amber-100">
                {legs.length} elite leg{legs.length === 1 ? "" : "s"}
                <span className="text-muted-foreground font-normal"> · DC + HT accumulator</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Same historical-market intelligence as Top 20 Elite, restricted to
                Double Chance + HT BTTS + HT O/U 0.5, stopping the moment we clear {target}x implied return.
              </div>
              {payload?.corpus_total > 0 && (
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Trained on <span className="font-semibold text-amber-200/70">{payload.corpus_total.toLocaleString()} all-time historical picks</span>.
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-3 text-right">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Product</div>
                <div className={["text-xl font-black tabular-nums", hitTarget ? "text-emerald-300" : "text-amber-300"].join(" ")} data-testid="x5elite-product">
                  {product.toFixed(2)}x
                </div>
              </div>
              <HitMissBar picks={legs} testid="x5elite-hitmiss" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              <Badge className={TIER_STYLES.A}>Tier A · {tierCounts.A || 0}</Badge>
              <Badge className={TIER_STYLES.B}>Tier B · {tierCounts.B || 0}</Badge>
              <Badge className={TIER_STYLES.C}>Tier C · {tierCounts.C || 0}</Badge>
              <Badge className={TIER_STYLES.D}>Tier D · {tierCounts.D || 0}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {legs.length === 0 && (
        <Card className="border-white/10">
          <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="x5elite-empty">
            No DC / HT candidate cleared the elite criteria for {date}.
            {" "}Considered {payload?.candidates_considered ?? 0} markets. Check back later or try tomorrow.
          </CardContent>
        </Card>
      )}

      {legs.map((p, i) => {
        const flag = countryToFlag(p.league_country) || "🌍";
        const oc = p.outcome;
        const outcomeTone =
          oc === true  ? "border-emerald-500/40 bg-emerald-500/[0.04]"
          : oc === false ? "border-rose-500/40 bg-rose-500/[0.04]"
          : "border-white/10";
        const oc_pill =
          oc === true  ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold uppercase tracking-wider" data-testid={`x5elite-outcome-${i}`}>Won</span>
          : oc === false ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 font-bold uppercase tracking-wider" data-testid={`x5elite-outcome-${i}`}>Lost</span>
          : null;
        return (
          <button
            key={`${p.match_id}-${p.market_type}-${i}`}
            type="button"
            onClick={() => onPickFixture?.({ match_id: p.match_id, fixture_id: p.fixture_id }, p.league_id)}
            data-testid={`x5elite-pick-${i}`}
            className="w-full text-left group"
          >
            <Card className={["hover:border-amber-500/30 transition", outcomeTone].join(" ")}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground w-6 tabular-nums">#{i + 1}</span>
                  <Badge className={["text-[10px] uppercase tracking-wide font-bold border", TIER_STYLES[p.tier] || ""].join(" ")}>
                    {TIER_LABELS[p.tier] || p.tier}
                  </Badge>
                  <span className="text-sm font-semibold text-white">
                    {p.market_type} <span className="text-emerald-300">→ {p.prediction}</span>
                  </span>
                  <LivePickTracker
                    fixtureId={p.fixture_id}
                    matchId={p.match_id}
                    marketType={p.market_type}
                    prediction={p.prediction}
                    testidPrefix="x5elite-live"
                  />
                  <span className="ml-auto flex items-center gap-2">
                    {oc_pill}
                    <Badge className={RISK_COLORS[p.risk_level] || ""}>{p.risk_level}</Badge>
                    <Badge variant="outline" className="text-xs font-bold border-white/20">
                      {Math.round(p.confidence)}%
                    </Badge>
                    <Badge variant="outline" className="text-xs font-bold border-amber-500/40 text-amber-200">
                      {p.implied_odds}x
                    </Badge>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-base leading-none">{flag}</span>
                  <span className="text-muted-foreground truncate">{p.league_name}</span>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="text-white truncate font-medium">
                    {p.home} <span className="text-muted-foreground">vs</span> {p.away}
                  </span>
                  {p.kickoff_iso && <span className="text-muted-foreground ml-auto">{fmtKickoff(p.kickoff_iso)}</span>}
                </div>
                <div className="flex items-center gap-3 text-[11px] flex-wrap pt-1 border-t border-white/[0.06]">
                  <span className="text-amber-200/90">
                    All-time <span className="font-bold">{p.historical_accuracy}%</span>
                    <span className="text-muted-foreground/70"> · n={p.sample_size}</span>
                  </span>
                  {p.recent_20_accuracy != null && (
                    <span className="text-violet-200/90">
                      Recent 20 <span className="font-bold">{p.recent_20_accuracy}%</span>
                    </span>
                  )}
                  {p.recent_10_accuracy != null && (
                    <span className="text-sky-200/90">
                      Recent 10 <span className="font-bold">{p.recent_10_accuracy}%</span>
                    </span>
                  )}
                  <span className="ml-auto text-white/80 text-xs">
                    Q-score <span className="font-bold">{p.quality_score}</span>
                  </span>
                </div>
                {p.red_flags?.length > 0 && (
                  <div className="text-[11px] text-amber-300/80">
                    ⚠ {p.red_flags.join(" · ")}
                  </div>
                )}
              </CardContent>
            </Card>
          </button>
        );
      })}
    </div>
  );
};

